import { useEffect, useState, useCallback } from "react";
import type { PlanEvent, Task } from "../types/index";
import { getToday } from "../types/date-helpers";
import { getNotificationSettings } from "../../features/settings/hooks/use-notification-settings";
import { isNative } from "../../lib/capacitor";
import { subscribeToPush } from "../../lib/push-subscription";

interface NotificationHookResult {
  permissionGranted: boolean;
  requestPermission: () => Promise<void>;
}

const TASK_REMINDER_HOUR = 9;

/** Notifications supportées (navigateur ou Capacitor natif) */
export const notificationsSupported = isNative || (typeof window !== "undefined" && "Notification" in window);

/** Cross-platform notification: natif Capacitor ou SW/constructeur web */
export async function sendNotification(body: string, tag: string): Promise<void> {
  if (isNative) {
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    const perms = await LocalNotifications.checkPermissions();
    if (perms.display !== "granted") {
      const req = await LocalNotifications.requestPermissions();
      if (req.display !== "granted") return;
    }
    await LocalNotifications.schedule({
      notifications: [{
        title: "Palnia",
        body,
        id: hashCode(tag),
        schedule: { at: new Date(Date.now() + 100) },
      }],
    });
    return;
  }

  if (!("Notification" in window) || Notification.permission !== "granted") return;

  // Prefer SW-based (works reliably on Windows/Linux)
  if ("serviceWorker" in navigator) {
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg?.active) {
        await reg.showNotification("Palnia", { body, tag, requireInteraction: false });
        return;
      }
    } catch { /* SW unavailable, fallback below */ }
  }

  // Fallback: direct constructor
  try {
    new Notification("Palnia", { body, tag, requireInteraction: false });
  } catch { /* permission revoked */ }
}

/** Convertit un tag string en ID numérique pour LocalNotifications */
function hashCode(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

export function useNotifications(
  events: readonly PlanEvent[],
  tasks: readonly Task[],
): NotificationHookResult {
  const [permissionGranted, setPermissionGranted] = useState(() => {
    if (isNative) return false; // vérifié async dans useEffect
    if (typeof window === "undefined" || !("Notification" in window)) return false;
    return Notification.permission === "granted";
  });

  // Vérifier les permissions (natif + web)
  useEffect(() => {
    if (isNative) {
      import("@capacitor/local-notifications").then(({ LocalNotifications }) => {
        LocalNotifications.checkPermissions().then((result) => {
          setPermissionGranted(result.display === "granted");
        });
      });
      return;
    }

    if (!("permissions" in navigator)) return;
    let status: PermissionStatus | null = null;
    const handleChange = () => {
      setPermissionGranted(status?.state === "granted");
    };
    navigator.permissions.query({ name: "notifications" }).then((s) => {
      status = s;
      handleChange();
      s.addEventListener("change", handleChange);
    }).catch(() => { /* API non supportée */ });
    return () => { status?.removeEventListener("change", handleChange); };
  }, []);

  // Event reminders
  useEffect(() => {
    if (!notificationsSupported || !permissionGranted) return;
    if (!getNotificationSettings().eventNotifications) return;

    const timeouts: ReturnType<typeof setTimeout>[] = [];
    const today = getToday();

    for (const event of events) {
      if (event.reminderMinutes === null) continue;
      if (event.date !== today) continue;

      const [hours, minutes] = event.startTime.split(":").map(Number);
      const eventDate = new Date();
      eventDate.setHours(hours, minutes, 0, 0);

      const reminderDate = new Date(
        eventDate.getTime() - event.reminderMinutes * 60000,
      );
      const now = new Date();
      const delay = reminderDate.getTime() - now.getTime();

      if (delay > 0) {
        const timeout = setTimeout(() => {
          void sendNotification(
            `Rappel: ${event.title} dans ${event.reminderMinutes} minutes`,
            `reminder-${event.id}`,
          );
        }, delay);

        timeouts.push(timeout);
      }
    }

    return () => {
      timeouts.forEach((timeout) => clearTimeout(timeout));
    };
  }, [events, permissionGranted]);

  // Task due date reminders
  useEffect(() => {
    if (!notificationsSupported || !permissionGranted) return;
    if (!getNotificationSettings().taskNotifications) return;

    const timeouts: ReturnType<typeof setTimeout>[] = [];
    const now = new Date();

    for (const task of tasks) {
      if (task.status === "done" || task.archived) continue;
      if (!task.dueDate || task.reminderMinutes == null) continue;

      // reminderMinutes for tasks: offset from due date at TASK_REMINDER_HOUR
      // 0 = due date at 9 AM, 1440 = 1 day before at 9 AM
      const [year, month, day] = task.dueDate.split("-").map(Number);
      const dueAtReminder = new Date(year, month - 1, day, TASK_REMINDER_HOUR, 0, 0, 0);
      const reminderTime = new Date(dueAtReminder.getTime() - task.reminderMinutes * 60000);
      const delay = reminderTime.getTime() - now.getTime();

      if (delay > 0 && delay < 86400000) {
        const timeout = setTimeout(() => {
          const label = task.reminderMinutes === 0
            ? "aujourd'hui"
            : task.reminderMinutes === 1440
              ? "demain"
              : `dans ${Math.round((task.reminderMinutes ?? 0) / 1440)} jours`;
          void sendNotification(
            `Tâche due ${label}: ${task.title}`,
            `task-reminder-${task.id}`,
          );
        }, delay);

        timeouts.push(timeout);
      }
    }

    return () => {
      timeouts.forEach((timeout) => clearTimeout(timeout));
    };
  }, [tasks, permissionGranted]);

  const requestPermission = useCallback(async (): Promise<void> => {
    if (isNative) {
      const { LocalNotifications } = await import("@capacitor/local-notifications");
      const result = await LocalNotifications.requestPermissions();
      setPermissionGranted(result.display === "granted");
      return;
    }

    if (!("Notification" in window)) return;

    if (Notification.permission === "granted") {
      setPermissionGranted(true);
      await subscribeToPush().catch(() => {});
      return;
    }

    if (Notification.permission === "default") {
      const permission = await Notification.requestPermission();
      setPermissionGranted(permission === "granted");
      if (permission === "granted") {
        await subscribeToPush().catch(() => {});
      }
    }
  }, []);

  return { permissionGranted, requestPermission };
}
